import { DocumentType } from '@consigli/types';
import { saveBlob } from '@consigli/utils';
import JSZip from 'jszip';
import { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { collectDocumentsIntoZip, isAbortError } from './zip-download';
export const useDownloadFilesAsZip = (zipName, translation = true, useNS3451Path = false) => {
    const { t } = useTranslation();
    const [isDownloading, setIsDownloading] = useState(false);
    const abortControllerRef = useRef(null);
    const getNS3451Path = useCallback((file) => {
        if (file.type === DocumentType.BLOB) {
            if (!file.ns3451 || file.ns3451.length === 0 || file.ns3451[0] === 'UNCATEGORIZED') {
                return `${t('ns3451.UNCATEGORIZED')}/${file.name}`;
            }
            const categories = file.ns3451[0].split('NS3451_')[1];
            let path = '';
            for (const index of categories.split('').keys()) {
                const subcategory = categories.substring(0, index + 1);
                path += `NS3451_${t('ns3451.NS3451_' + subcategory)}/`;
            }
            path += file.name;
            return path;
        }
        return '';
    }, [t]);
    const downloadFileZip = useCallback(async (documents) => {
        setIsDownloading(true);
        toast.info(translation
            ? t('document-list.download-begin', { n: documents.length })
            : `Downloading ${documents.length} files...`);
        abortControllerRef.current = new AbortController();
        const { signal } = abortControllerRef.current;
        try {
            const zip = new JSZip();
            const processedDocuments = documents.map((doc) => ({
                ...doc,
                name: useNS3451Path ? getNS3451Path(doc) : doc.name,
            }));
            const failedNames = await collectDocumentsIntoZip(processedDocuments, zip, signal);
            if (documents.length > 0 && failedNames.length === documents.length) {
                // Nothing arrived, so there is no archive worth saving. Handled below
                // like any other failure of the whole download.
                throw new Error(`None of the ${documents.length} files could be downloaded`);
            }
            const zipBlob = await zip.generateAsync({ type: 'blob' });
            saveBlob(zipName + '.zip', zipBlob);
            if (failedNames.length > 0) {
                // The archive is short of some files, so say so rather than let it
                // look complete. Which ones is in the console.
                toast.warning(translation ? t('document-list.download-failed') : 'Download failed');
            }
        }
        catch (error) {
            if (isAbortError(error)) {
                toast.info(translation ? t('document-list.download-aborted') : 'Download aborted');
            }
            else {
                toast.error(translation
                    ? t('document-list.download-failed')
                    : `Failed to download files. Error: ${error}`);
            }
        }
        finally {
            setIsDownloading(false);
        }
    }, [getNS3451Path, t, translation, useNS3451Path, zipName]);
    const abortDownload = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
    }, []);
    return {
        isDownloading,
        downloadFileZip,
        abortDownload,
    };
};
